import { startTransition, useCallback } from "react";

/**
 * Decouples a user keystroke from the downstream React work an edit triggers.
 *
 * ## The contract
 * Editable fields keep a synchronous LOCAL buffer (see `EditableTextCell`,
 * `MoneyInput`, `PercentInput`) so the typed character paints immediately. The
 * *commit* returned here pushes that change into the Source-of-Truth, but wraps
 * the SoT mutation in `startTransition` so its expensive consequences — the
 * full-table re-render, derived recalcs, the autosave-signature recompute — run
 * in a LOW-PRIORITY pass that React can interrupt for the next keystroke. So
 * typing never lags, no matter how heavy the table.
 *
 * ## Why it's safe
 * The SoT hooks (`useLossRuns`, `useFacilities`, …) mutate a synchronous
 * in-memory store *inside* `commit` and only THEN notify React subscribers via
 * setState. `startTransition` defers only those setState calls (the re-render);
 * the store write already happened synchronously. So `save()` — which reads the
 * store, not React state — always sees current data, and the debounced autosave
 * still fires correctly.
 *
 * `commit` may return a Promise (the staged-update hooks are async for signature
 * reasons but do no awaiting before the store write). We discard it with `void`
 * — these edits are fire-and-forget; we explicitly do NOT want React 19's
 * async-transition "stay pending until the promise settles" semantics here.
 *
 * This hook is the SINGLE place the deferral policy lives — change strategy here
 * (e.g. switch to `useTransition` to expose an `isPending` flag, add telemetry)
 * and every editable section inherits it.
 *
 * Returns a stable callback (depends only on `commit`), so `React.memo` rows
 * that receive it as a prop keep bailing out of re-renders.
 */
export function useDeferredSetter<A extends unknown[]>(commit: (...args: A) => unknown): (...args: A) => void {
    return useCallback(
        (...args: A) => {
            startTransition(() => {
                void commit(...args);
            });
        },
        [commit],
    );
}
